import { fetchSquadsForTournament } from "./fetchSquadsForTournament";
import { fetchTeamsForSquads } from "./fetchTeamsForSquads";
import { checkTeamsScoresComplete } from "./checkTeamsScoresComplete";
import { fetchEventFees } from "./fetchEventFees";
import { fetchEventIdsForTournament } from "./fetchEventIdsForTournament";
import { fetchTournamentFinalizationFlags } from "./fetchTournamentFinalizationFlags";
import { processVerifiedAverages } from "./processVerifiedAverages";
import { processVerifiedTeams } from "./processVerifiedTeams";
import { fetchTournamentSignupSettings } from "../../signupSettings";
import { onlineSignupsEnabled } from "../../signupEnablement";

export async function processFinalization(tournamentId) {
  if (!tournamentId) throw new Error("No tournament ID provided.");

  try {
    const tournament = await fetchTournamentFinalizationFlags(tournamentId);

    const eventIds = await fetchEventIdsForTournament(tournamentId);
    const hasEvents = eventIds.length > 0;

    let eventFees = [];
    if (hasEvents) {
      eventFees = await fetchEventFees(eventIds);
    }
    const eventFeeMap = new Map();
    eventFees.forEach((fee) => {
      if (!eventFeeMap.has(fee.eventsID)) {
        eventFeeMap.set(fee.eventsID, []);
      }
      eventFeeMap.get(fee.eventsID).push(fee);
    });
    const allEventsHaveFees = eventIds.every((id) => eventFeeMap.has(id));

    const squads = await fetchSquadsForTournament(tournamentId);
    const squadIds = squads.map((squad) => squad.id);
    const hasSquads = squadIds.length > 0;
    const squadDates = squads
      .map((squad) => squad.squadDate)
      .filter(Boolean)
      .sort();
    const firstSquadDate = squadDates[0] || null;
    const lastSquadDate = squadDates[squadDates.length - 1] || null;

    let teams = [];
    if (hasSquads) {
      teams = await fetchTeamsForSquads(squadIds);
    }
    const squadsWithTeams = new Set(teams.map((team) => team.squadID));
    const allSquadsHaveTeams =
      hasSquads && squadIds.every((id) => squadsWithTeams.has(id));

    let allTeamScoresComplete = false;
    if (allSquadsHaveTeams) {
      allTeamScoresComplete = await checkTeamsScoresComplete(teams);
    }

    const signupSettings = await fetchTournamentSignupSettings(tournamentId);
    const allowSignups = onlineSignupsEnabled(signupSettings);

    const detailsComplete =
      tournament.payoutTableId !== null &&
      tournament.payoutRatioNumerator !== null &&
      tournament.payoutRatioDenominator !== null;

    const averagesVerified = await processVerifiedAverages(tournamentId);
    const teamsVerified = averagesVerified
      ? await processVerifiedTeams(eventIds)
      : false;

    const payoutFinalized = teamsVerified && tournament.isPayoutFinalized;
    const resultsFinalized = payoutFinalized && tournament.isResultsFinalized;
    const tournamentFinalized =
      resultsFinalized && tournament.isTournamentFinalized;

    return {
      detailsComplete,
      hasEvents,
      allEventsHaveFees,
      hasSquads,
      allSquadsHaveTeams,
      allTeamScoresComplete,
      averagesVerified,
      teamsVerified,
      payoutFinalized,
      resultsFinalized,
      tournamentFinalized,
      firstSquadDate,
      lastSquadDate,
      allowSignups,
    };
  } catch (error) {
    console.error("❌ ERROR: processFinalization failed:", error.message);
    throw error;
  }
}
